/**
 * HeroSection — Morgan Legal Group
 * Design: Full-bleed split layout, navy left panel with headline, right with NYC skyline image
 * Animated word-by-word headline reveal, scroll indicator
 */
import { Fragment, useEffect, useState } from "react";
import { ArrowDown, Phone, Calendar, Star } from "lucide-react";
import CalendarEmbed from "@/components/CalendarEmbed";

const words = ["Your", "Trusted", "Estate", "Planning", "Attorney", "in", "New", "York"];

export default function HeroSection() {
  const [visibleWords, setVisibleWords] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleWords((prev) => {
          if (prev >= words.length) {
            clearInterval(interval);
            setTimeout(() => setStatsVisible(true), 200);
            return prev;
          }
          return prev + 1;
        });
      }, 90);
      return () => clearInterval(interval);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const handleConsultation = () => {
    const el = document.querySelector("#consultation");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = () => {
    const el = document.querySelector("#stats");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative min-h-screen flex flex-col lg:flex-row pt-16 lg:pt-20">
      {/* Left Panel — Navy */}
      <div className="relative z-10 flex flex-col justify-center bg-navy px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-24 w-full lg:w-[55%] xl:w-[52%]">
        {/* Background watermark text */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <span className="absolute -bottom-8 -left-4 font-display text-[12rem] lg:text-[18rem] font-bold text-white/[0.025] leading-none whitespace-nowrap">
            JUSTICE
          </span>
        </div>

        <div className="relative z-10 max-w-xl">
          {/* Section label */}
          <div className="section-label mb-6 text-gold">
            <span className="block w-10 h-0.5 bg-gold" />
            Attorneys at Law · New York City
          </div>

          {/* Main headline */}
          {/*
            🔴 Пробел между словами — НАСТОЯЩИЙ, а не отступ `mr-[0.25em]`.
            Слова разнесены по <span> ради появления по одному; отступ рисовал
            промежуток на экране, но в разметке его не было, и заголовок
            извлекался как «YourTrustedEstatePlanningAttorneyinNewYork» — так его
            читают экранный диктор, краулер и любой разбор текста страницы.
            Найдено внешним аудитом 03.09.2026; глаз этого не видит по
            построению. `{" "}` вне анимируемого span, поэтому появление слов
            выглядит по-прежнему.
          */}
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6">
            {words.map((word, i) => (
              <Fragment key={i}>
                <span
                  className={`inline-block transition-all duration-500 ${
                    i < visibleWords
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {word === "New" || word === "York" ? (
                    <span className="text-gold">{word}</span>
                  ) : (
                    word
                  )}
                </span>
                {i < words.length - 1 ? " " : null}
              </Fragment>
            ))}
          </h1>

          {/* Subheadline */}
          <p
            className={`font-body text-white/75 text-base lg:text-lg leading-relaxed mb-8 transition-all duration-700 ${
              statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Morgan Legal Group has served New York families since 2017, with over 100 years of combined legal experience across our attorneys. We bring sustained experience in estate planning, probate, elder law, family law, and real estate.
          </p>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap gap-3 mb-8 transition-all duration-700 delay-100 ${
              statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {["4.9 ★ Google Rating", "900+ Reviews, All Platforms", "100+ Years Combined Experience", "Free Consultation"].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 bg-white/10 border border-gold/30 text-white/90 text-xs font-body font-medium px-3 py-1.5 rounded-sm"
              >
                {badge.includes("★") && <Star className="w-3 h-3 fill-gold text-gold" />}
                {badge}
              </span>
            ))}
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-3 mb-10 transition-all duration-700 delay-200 ${
              statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <button onClick={handleConsultation} className="btn-gold">
              <Calendar className="w-4 h-4" />
              Schedule Your Consultation
            </button>
            <a href="tel:8885291315" className="btn-ghost-gold">
              <Phone className="w-4 h-4" />
              888-529-1315
            </a>
          </div>

          {/* Attorney signature */}
          <div
            className={`flex items-center gap-4 pt-6 border-t border-white/10 transition-all duration-700 delay-300 ${
              statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {/* Rendered at 48px. The 640px master was 49KB of bandwidth stolen
                from the LCP image; this crop is 1.8KB. */}
            <picture className="flex-shrink-0">
              <source srcSet="/img/attorneys/russel-morgan-headshot-96.webp" type="image/webp" />
              <img
                src="/img/attorneys/russel-morgan-headshot.jpg"
                alt="Russel Morgan, Esq. — CEO & Founder, Morgan Legal Group"
                width="96"
                height="96"
                loading="lazy"
                className="w-12 h-12 rounded-full object-cover border-2 border-gold/40"
              />
            </picture>
            <div>
              <p className="font-serif-elegant text-white text-lg italic">Russel Morgan</p>
              <p className="font-body text-white/60 text-xs tracking-wide uppercase">CEO & Founder · Morgan Legal Group</p>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={handleScroll}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 lg:left-16 xl:left-20 lg:translate-x-0 flex flex-col items-center gap-2 text-white/40 hover:text-gold transition-colors duration-200 group"
        >
          <span className="font-body text-xs tracking-widest uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </button>
      </div>

      {/* Right Panel — NYC skyline + Schedule card */}
      <div className="relative w-full lg:w-[50%] xl:w-[50%] min-h-[80vh] lg:min-h-0 bg-navy overflow-hidden">
        {/* LCP element. It occupies half the viewport, so a single 1920px file was
            180KB of largest-paint on a page that otherwise weighs 600KB; the
            srcset lets a laptop take the 75KB copy. */}
        <img
          src="/img/sections/hero-nyc-skyline-1280.webp"
          srcSet="/img/sections/hero-nyc-skyline-1280.webp 1280w, /img/sections/hero-nyc-skyline-1920.webp 1920w"
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt=""
          width="1200"
          height="900"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          aria-hidden="true"
        />
        {/* Left edge fade into navy panel */}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-navy/15 to-navy/45 pointer-events-none" aria-hidden="true" />
        {/* Bottom navy fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-navy to-transparent pointer-events-none" aria-hidden="true" />

        {/* Schedule Your Consultation — OS calendar (centered, sized to content) */}
        <div className="absolute inset-0 flex items-center justify-center p-4 lg:px-8">
          <div className="w-full max-w-md bg-white shadow-2xl overflow-hidden">
            <CalendarEmbed height={500} loading="facade" />
          </div>
        </div>

        {/* Floating info card */}
        <div className="absolute bottom-8 right-6 lg:bottom-12 lg:right-8 bg-white/95 backdrop-blur-sm p-4 lg:p-5 shadow-2xl max-w-[220px]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-navy flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-gold" />
            </div>
            <div>
              <p className="font-body text-navy text-xs font-semibold uppercase tracking-wide mb-0.5">Call Us Now</p>
              <p className="font-display text-navy text-base font-bold">888-529-1315</p>
              <p className="font-body text-navy/60 text-xs mt-0.5">Answered 24/7 · Attorneys Mon–Fri, 9am–6pm</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-3 h-3 fill-gold text-gold" />
              ))}
              <span className="font-body text-navy/70 text-xs ml-1">4.9 average on Google</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
